import ky, { type KyInstance } from "ky";
import { withCardBase } from "@/app/base-path";
import { refreshStaffSession } from "@/features/auth/api/auth-staff";
import {
	applyStaffAccess,
	clearSession,
	getAccessToken,
	getRefreshToken,
} from "@/features/auth/lib/auth-store";
import { getApiBaseUrl } from "@/shared/api/config";

let refreshInFlight: Promise<boolean> | null = null;

async function tryRefreshStaffAccess(): Promise<boolean> {
	const refreshToken = getRefreshToken();
	if (!refreshToken) {
		return false;
	}
	if (!refreshInFlight) {
		refreshInFlight = refreshStaffSession(refreshToken)
			.then((next) => {
				applyStaffAccess(next);
				return true;
			})
			.catch(() => false)
			.finally(() => {
				refreshInFlight = null;
			});
	}
	return refreshInFlight;
}

function bounceToLogin(): void {
	clearSession();
	const path = window.location.pathname;
	const base = withCardBase("/").replace(/\/$/, "");
	const rest = base && path.startsWith(base) ? path.slice(base.length) : path;
	const next = rest.startsWith("/") ? rest : `/${rest || ""}`;
	const loginPath = withCardBase(
		next && next !== "/" ? `/login?next=${encodeURIComponent(next)}` : "/login",
	);
	if (!window.location.pathname.endsWith("/login")) {
		window.location.assign(loginPath);
	}
}

/** Public card reads — no 401 bounce (view works without staff). */
export const publicApi: KyInstance = ky.create({
	prefixUrl: getApiBaseUrl(),
});

export const api: KyInstance = ky.create({
	prefixUrl: getApiBaseUrl(),
	hooks: {
		beforeRequest: [
			(request) => {
				const token = getAccessToken();
				if (token) {
					request.headers.set("Authorization", `Bearer ${token}`);
				}
			},
		],
		afterResponse: [
			async (request, options, response) => {
				if (response.status !== 401) {
					return response;
				}

				if (getRefreshToken()) {
					const refreshed = await tryRefreshStaffAccess();
					if (refreshed) {
						const token = getAccessToken();
						const retry = new Request(request, options as RequestInit);
						if (token) {
							retry.headers.set("Authorization", `Bearer ${token}`);
						}
						return ky(retry);
					}
				}

				bounceToLogin();
				return response;
			},
		],
	},
});

export type ApiErrorBody = {
	statusCode?: number;
	message?: string | string[];
	error?: string;
	code?: string;
	balance?: number;
	pointsCost?: number;
};

export async function readApiError(response: Response): Promise<ApiErrorBody> {
	try {
		return (await response.json()) as ApiErrorBody;
	} catch {
		return { statusCode: response.status, message: response.statusText };
	}
}
